import { NO_HANDLE, TICK_MS, VitalsState, effectByNumber, regenerationSlack, type EffectsRecord, type ResourceId, type Rules, type TargetEffectsRecord, type VitalsRecord } from "odyssey-shared";
import type { GlyphName } from "../../ui/kit/Glyph.tsx";
import { limitsOfFrame } from "../fight/fight.ts";
import { BAR_SLOT_COUNT, EFFECT_GLYPHS, barSlots, effectTone, slotUsable, type BarSlotSpec, type PlayerState, type SlotUsable } from "./layout.ts";
import { BarPages } from "./pages.ts";
import { RecordCooldowns } from "./record-cooldowns.ts";
import { refusalWord } from "./words.ts";

/**
 * The ability bar's, the cast bar's and the status rows' store (P2.2-T4): what they draw, owned by the game, telling
 * React only when something it draws changed, as the HUD's store does. Everything the server says goes through it and
 * nothing is predicted (the Phase 2 spec's 3.2): a slot's cooldown runs from a `used` to its receipt time plus the ticks
 * the event carries, which is after the server's ready tick and never before, and nothing is drawn for an act between
 * its send and its answer but the windup the fight plays; an effect's time left runs from the receipt of the frame that
 * named it, and stands still while its bearer is dead, as the server freezes it. What changes by the frame (the
 * cooldown's wipe, the seconds, the times left) lives in typed arrays allocated once (`BarTimes`), which `ticker.ts`
 * writes into the DOM, so no frame allocates. The times are whole ms from the store's origin on the page clock, so every
 * value the ticker reads and computes is a small integer V8 keeps unboxed (to about 12 days of a page open).
 *
 * The slots are what `setSlots` names (`resolve.ts`): the player's bar, or the default bar, with the items the bag
 * holds (P2.8-T29, ADR-121 decision 9; ADR-091 decision 14). A slot holding an item wipes for the cooldown of the record
 * the item's use runs, from the server's `used` as every slot does. Each record's cooldown is kept by its number
 * (`record-cooldowns.ts`), so a slot that is moved, empties or fills again inside a cooldown shows what is left of it,
 * and two slots holding one thing sweep together.
 */

/**
 * The most effects a row keeps: a character's effect slots on the map server.
 */
export const STATUS_MAX = 8;

/**
 * How long a refused act's reason stays on the cast bar, in ms (a draft knob, as the message line's 4 s is).
 */
export const CAST_REASON_MS = 1500;

/**
 * How long before its end an effect's time turns to the warning colour and blinks, in ms; only an effect that lasts
 * longer than this does (the design's `expiring` state; a draft knob), so a short stun never blinks.
 */
export const EXPIRING_MS = 5000;

/**
 * A slot as drawn: the rules' slot and what it can do now.
 */
export interface BarSlotView extends BarSlotSpec {
  readonly usable: SlotUsable;
}

/**
 * An effect as a row draws it: its number, name, glyph (null for none), icon key (null for none; ADR-111 decision
 * 1), whether it is a buff or a debuff, and its stacks.
 */
export interface EffectView {
  readonly effect: number;
  readonly name: string;
  readonly glyph: GlyphName | null;
  readonly icon: string | null;
  readonly tone: "buff" | "debuff";
  readonly stacks: number;
}

/**
 * The cast bar's refused state: the ability's name, the reason's words, and a serial that moves with each refusal so a
 * repeated reason draws again.
 */
export interface CastView {
  readonly name: string;
  readonly reason: string;
  readonly serial: number;
}

/**
 * What the bar, the cast bar and the rows draw: the slots, the cast bar's refusal (null for none), the player's
 * effects and its target's (empty for none), and whether the player's own times stand still (dead).
 */
export interface BarSnapshot {
  readonly slots: readonly BarSlotView[];
  readonly cast: CastView | null;
  readonly own: readonly EffectView[];
  readonly target: readonly EffectView[];
  readonly frozen: boolean;
}

/**
 * What a row's times are: for each effect drawn, when it ends, in whole ms from the origin, or, while its bearer is
 * dead, the ms it has left, and whether it may turn to expiring; and, for the tooltip, each effect's number and the longest
 * time left in ticks the row has seen of it while it stayed (`span`), which tells a running effect's duration.
 */
export interface RowTimes {
  count: number;
  readonly effect: Int32Array;
  readonly span: Uint16Array;
  readonly end: Int32Array;
  readonly left: Int32Array;
  readonly expires: Uint8Array;
}

/**
 * What the ticker reads each frame: the origin on the page clock every time is counted from, in whole ms; each slot's
 * cooldown end (0 for none) and its length in ms; the rows' times; and whether the player's are frozen.
 */
export interface BarTimes {
  readonly origin: number;
  readonly cooldownEnd: Int32Array;
  readonly cooldownLength: Int32Array;
  readonly own: RowTimes;
  readonly target: RowTimes;
  frozen: boolean;
}

/**
 * What the bar's view reads: the rules its tooltips are drafted from, what to draw, a way to hear that it changed, the
 * times, the page shown where the bar pages (`pages.ts`), and a way to use a slot.
 */
export interface BarSource {
  readonly rules: Rules;
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => BarSnapshot;
  readonly times: BarTimes;
  readonly pages: BarPages;
  /**
   * Uses a slot, as a click on it does.
   *
   * @param slot The slot's index, from 0.
   * @returns True when its act was sent.
   */
  use(slot: number): boolean;
}

/**
 * Runs `end` once after `ms`, returning a way to cancel it: how the store ends the cast bar's reason off the frame
 * path, so no frame allocates for it.
 */
export type CastTimer = (end: () => void, ms: number) => () => void;

/**
 * The page's cast timer, on `setTimeout`.
 *
 * @param end What to run.
 * @param ms After how long.
 * @returns Cancels it.
 */
const PAGE_CAST_TIMER: CastTimer = (end, ms) => {
  const timer = setTimeout(end, ms);
  return () => clearTimeout(timer);
};

/**
 * The effects and spans a row held before a frame refills it, so an effect that stays keeps its span.
 */
const PRIOR_EFFECT = new Int32Array(STATUS_MAX);
const PRIOR_SPAN = new Uint16Array(STATUS_MAX);

/**
 * A row's times, allocated once.
 *
 * @returns Empty times for `STATUS_MAX` effects.
 */
function rowTimes(): RowTimes {
  return { count: 0, effect: new Int32Array(STATUS_MAX), span: new Uint16Array(STATUS_MAX), end: new Int32Array(STATUS_MAX), left: new Int32Array(STATUS_MAX), expires: new Uint8Array(STATUS_MAX) };
}

/**
 * Whether two slots draw the same: the same thing, the same count and the same state of its item.
 *
 * @param a One slot.
 * @param b The other.
 * @returns True when nothing drawn differs.
 */
function sameSpec(a: BarSlotSpec, b: BarSlotSpec): boolean {
  return a.ability === b.ability && a.item === b.item && a.count === b.count && a.spent === b.spent && a.name === b.name && a.icon === b.icon && a.glyph === b.glyph && a.basic === b.basic && a.cost?.amount === b.cost?.amount && a.cost?.resource === b.cost?.resource;
}

/**
 * Whether two lists of effects draw the same.
 *
 * @param a One list.
 * @param b The other.
 * @returns True when every entry's number and stacks match.
 */
function sameEffects(a: readonly EffectView[], b: readonly EffectView[]): boolean {
  return a.length === b.length && a.every((entry, index) => entry.effect === b[index]?.effect && entry.stacks === b[index]?.stacks);
}

/**
 * The slack before the first `vitals`: none, with every pool unknown, so nothing is short.
 */
const NO_SLACK: Readonly<Record<ResourceId, number>> = { health: 0, stamina: 0, focus: 0 };

/**
 * The store.
 *
 * @example
 * ```ts
 * const bar = new BarStore(PAGE_RULES);
 * bar.used(1, 10, performance.now());
 * ```
 */
export class BarStore implements BarSource {
  private snapshot: BarSnapshot;
  private forcedOwn: EffectsRecord["effects"][number] | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly specs: BarSlotSpec[];
  private readonly records: RecordCooldowns;
  private player: PlayerState = { dead: false, limits: 0, pools: { health: Number.POSITIVE_INFINITY, stamina: Number.POSITIVE_INFINITY, focus: Number.POSITIVE_INFINITY }, slack: NO_SLACK };
  private targetHandle = NO_HANDLE;
  private castSerial = 0;
  private cancelCast: (() => void) | null = null;
  private useSlot: (slot: number) => boolean = () => false;
  private useItem: (item: string) => boolean = () => false;
  public readonly times: BarTimes;
  public readonly pages = new BarPages();

  /**
   * @param rules The page's rules, which name the bar's abilities, their costs and the effects, and which the tooltips
   *   read (P2.2-T12).
   * @param origin The page clock every time is counted from, in ms: now, on the page.
   * @param castTimer Ends the cast bar's reason after its time; `setTimeout` on the page, a still one in the gallery.
   */
  public constructor(
    public readonly rules: Rules,
    origin: number = performance.now(),
    private readonly castTimer: CastTimer = PAGE_CAST_TIMER,
  ) {
    this.times = { origin: Math.floor(origin), cooldownEnd: new Int32Array(BAR_SLOT_COUNT), cooldownLength: new Int32Array(BAR_SLOT_COUNT), own: rowTimes(), target: rowTimes(), frozen: false };
    this.specs = [...barSlots(rules)];
    this.records = new RecordCooldowns(rules.tables.abilities.length);
    this.snapshot = { slots: this.specs.map((spec) => ({ ...spec, usable: slotUsable(spec, this.player) })), cast: null, own: [], target: [], frozen: false };
  }

  /**
   * Adds a listener told of every change. Works unbound, as React calls it.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the bar draws now: the same object until something changes. Works unbound, as React calls it.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): BarSnapshot => this.snapshot;

  /**
   * Binds the way a click on a slot reaches the fight: the session's, while there is one.
   *
   * @param use Uses a slot by its index, answering whether its act was sent.
   */
  public bindUse(use: (slot: number) => boolean): void {
    this.useSlot = use;
  }

  /**
   * Binds the way a use of an item slot reaches the bag: the session's, while there is one.
   *
   * @param use Asks the server to use the first stack of an item, answering whether a request was sent.
   */
  public bindUseItem(use: (item: string) => boolean): void {
    this.useItem = use;
  }

  /**
   * Whether a slot holds an item now, so its key and its click use the item and never an ability.
   *
   * @param slot The slot's index, from 0.
   * @returns True for a slot naming an item, at 0 included.
   */
  public holdsItem(slot: number): boolean {
    return (this.specs[slot]?.item ?? null) !== null;
  }

  /**
   * Uses a slot, as a click on it does: an item slot asks the server to use the first stack of its item, and sends
   * nothing while it is at 0 (ADR-121 decision 1.2); any other sends its act.
   *
   * @param slot The slot's index, from 0.
   * @returns True when its act or its request was sent.
   */
  public use(slot: number): boolean {
    const spec = this.specs[slot];
    if (spec === undefined) {
      return false;
    }
    if (spec.item !== null) {
      return !spec.spent && this.useItem(spec.item);
    }
    return this.useSlot(slot);
  }

  /**
   * The slots the bar draws now, as `resolve.ts` gives them.
   *
   * @returns The slots, in slot order.
   */
  public slotSpecs(): readonly BarSlotSpec[] {
    return this.specs;
  }

  /**
   * Sets what every slot holds (`resolve.ts`): the player's bar or the default bar, with the bag's counts. Each slot
   * takes the cooldown its ability's record has left, as the last `used` for it said, so a moved slot keeps its sweep;
   * an item slot at 0 draws none (ADR-121 decision 1.2) and takes what is left again when the bag holds the item.
   * Nothing is told when no slot changed.
   *
   * @param specs The ten slots, in slot order.
   */
  public setSlots(specs: readonly BarSlotSpec[]): void {
    let changed = false;
    for (let index = 0; index < BAR_SLOT_COUNT; index += 1) {
      const next = specs[index];
      const before = this.specs[index];
      if (next === undefined || before === undefined) {
        continue;
      }
      if (sameSpec(before, next)) {
        continue;
      }
      changed = true;
      this.specs[index] = next;
      this.times.cooldownEnd[index] = next.spent ? 0 : this.records.endOf(next.ability);
      this.times.cooldownLength[index] = next.spent ? 0 : this.records.lengthOf(next.ability);
    }
    if (changed) {
      this.change({ ...this.snapshot, slots: this.specs.map((spec) => ({ ...spec, usable: slotUsable(spec, this.player) })) });
    }
  }

  /**
   * Reads the player's own `vitals`: whether it is dead, and each resource, which decide whether a slot is locked or
   * short of its cost. The row's times freeze and run again only with the `effects` frame the server sends after a
   * death or a stand, which carries the times they freeze or run from, so no frame between the two shows a stale time.
   *
   * @param vitals The decoded frame.
   */
  public setVitals(vitals: Readonly<VitalsRecord>): void {
    const dead = (vitals.state & VitalsState.Dead) !== 0;
    const pools: Record<ResourceId, number> = { health: vitals.health, stamina: vitals.stamina, focus: vitals.focus };
    const slack: Record<ResourceId, number> = { health: regenerationSlack(this.rules, "health", vitals.maxHealth), stamina: regenerationSlack(this.rules, "stamina", vitals.maxStamina), focus: regenerationSlack(this.rules, "focus", vitals.maxFocus) };
    const before = this.player;
    if (before.dead === dead && before.pools.health === pools.health && before.pools.stamina === pools.stamina && before.pools.focus === pools.focus && before.slack.health === slack.health && before.slack.stamina === slack.stamina && before.slack.focus === slack.focus) {
      return;
    }
    this.player = { ...before, dead, pools, slack };
    this.refreshSlots({});
  }

  /**
   * Reads the limits the player's effects set (`fight/fight.ts`'s `limitsOfFrame`).
   *
   * @param limits The `EffectLimit` bits.
   */
  public setLimits(limits: number): void {
    if (limits !== this.player.limits) {
      this.player = { ...this.player, limits };
      this.refreshSlots({});
    }
  }

  /**
   * Reads a `used` for the player's own act or item: every slot holding the ability wipes from now to the receipt time
   * plus the ticks the event carries, and the record's cooldown is kept by its number for the item slot. Nothing React
   * draws changes.
   *
   * @param ability The ability's number.
   * @param cooldownTicks The event's ticks until ready.
   * @param nowMs The page clock at receipt.
   */
  public used(ability: number, cooldownTicks: number, nowMs: number): void {
    const length = cooldownTicks * TICK_MS;
    this.cool(ability, length > 0 ? this.since(nowMs) + length : 0, length);
  }

  /**
   * Reads a `cooldown` told at the join for a cooldown running as the player joined (ADR-084's note for P2.3-T8):
   * every slot holding the ability wipes from now to the receipt time plus the ticks left, over the ability's whole
   * cooldown, or the ticks left when its effects made them longer, so the wipe starts at the share still to run.
   *
   * @param ability The ability's number.
   * @param ticksLeft The event's ticks until ready.
   * @param nowMs The page clock at receipt.
   */
  public told(ability: number, ticksLeft: number, nowMs: number): void {
    const left = ticksLeft * TICK_MS;
    const length = Math.max(left, (this.rules.tables.abilities[ability - 1]?.cooldownTicks ?? 0) * TICK_MS);
    this.cool(ability, left > 0 ? this.since(nowMs) + left : 0, left > 0 ? length : 0);
  }

  /**
   * Sets every slot holding an ability to wipe until a time over a length, and keeps the two by the record's number
   * for the item slot. Nothing React draws changes.
   *
   * @param ability The ability's number.
   * @param end The cooldown's end on the store's clock, 0 for none.
   * @param length The wipe's whole length in ms, 0 for none.
   */
  private cool(ability: number, end: number, length: number): void {
    this.records.note(ability, end, length);
    for (const spec of this.specs) {
      if (spec.ability === ability && ability !== 0 && !spec.spent) {
        this.times.cooldownEnd[spec.index] = end;
        this.times.cooldownLength[spec.index] = length;
      }
    }
  }

  /**
   * Reads a `refused` for the player's own act. A refusal that cancelled the windup it answers shows the reason on the
   * cast bar for `CAST_REASON_MS` (the design: "a rejected cast cancels the windup and the reason shows on the bar and
   * the message line"), ended by the cast timer, which a newer refusal restarts; one answering an older act, whose
   * windup is long over, shows nothing here.
   *
   * @param ability The ability's number.
   * @param reason The reason code.
   * @param cancelled Whether it cancelled the windup playing.
   */
  public refused(ability: number, reason: number, cancelled: boolean): void {
    if (!cancelled) {
      return;
    }
    this.castSerial += 1;
    this.cancelCast?.();
    this.cancelCast = this.castTimer(this.endCast, CAST_REASON_MS);
    const name = this.rules.tables.abilities[ability - 1]?.name ?? "";
    this.change({ ...this.snapshot, cast: { name, reason: refusalWord(reason), serial: this.castSerial } });
  }

  /**
   * Clears the cast bar's reason once its time is up, as the cast timer calls it. Bound, so the timer holds it as is.
   */
  private readonly endCast = (): void => {
    this.cancelCast = null;
    if (this.snapshot.cast !== null) {
      this.change({ ...this.snapshot, cast: null });
    }
  };

  /**
   * Adds one effect to every owner frame this page reads, for a development page's mockup force (`?drenched=`): it is
   * drawn as if the server had listed it and changes nothing the server decides.
   *
   * @param entry The effect, its ticks left and stacks, or null for none.
   */
  public forceOwnEffect(entry: EffectsRecord["effects"][number] | null): void {
    this.forcedOwn = entry;
  }

  /**
   * Reads the owner's `effects` frame: the player's effects, each running from now, or standing still while it is dead,
   * and the limits they set, which lock the slots while acting is forbidden.
   *
   * @param record The decoded frame.
   * @param nowMs The page clock at receipt.
   */
  public setOwnEffects(record: Readonly<EffectsRecord>, nowMs: number): void {
    this.setLimits(limitsOfFrame(this.rules, record));
    const forced = this.forcedOwn;
    const entries = forced === null ? record.effects : [...record.effects.slice(0, record.count), forced];
    const own = this.fill(entries, forced === null ? record.count : record.count + 1, this.times.own, nowMs, this.player.dead);
    this.times.frozen = this.player.dead;
    if (!sameEffects(own, this.snapshot.own) || this.snapshot.frozen !== this.player.dead) {
      this.change({ ...this.snapshot, own, frozen: this.player.dead });
    }
  }

  /**
   * How long one of the player's own effects has left on the page clock, as its row counts it: what the drip under a
   * Drenched character thins by (ADR-144's amendment of 2026-11-01, decision 7).
   *
   * @param effect The effect's number.
   * @param nowMs The page clock.
   * @returns The ms left, the time it held at its bearer's death while it is dead, 0 when it does not bear it.
   */
  public leftMs(effect: number, nowMs: number): number {
    const times = this.times.own;
    for (let at = 0; at < times.count; at += 1) {
      if (times.effect[at] === effect) {
        return this.times.frozen ? (times.left[at] as number) : Math.max(0, (times.end[at] as number) - this.since(nowMs));
      }
    }
    return 0;
  }

  /**
   * Sets the player's selection, as the scene does every frame: a change empties the target's row until a
   * `targetEffects` frame names the new one.
   *
   * @param handle The selected handle, `NO_HANDLE` for none.
   */
  public setTarget(handle: number): void {
    if (handle === this.targetHandle) {
      return;
    }
    this.targetHandle = handle;
    this.times.target.count = 0;
    if (this.snapshot.target.length > 0) {
      this.change({ ...this.snapshot, target: [] });
    }
  }

  /**
   * Reads a `targetEffects` frame: the selected target's visible effects, each running from now. A frame naming another
   * handle than the selection, one sent before a change of selection reached the server, is dropped.
   *
   * @param record The decoded frame.
   * @param nowMs The page clock at receipt.
   */
  public setTargetEffects(record: Readonly<TargetEffectsRecord>, nowMs: number): void {
    if (record.target !== this.targetHandle || record.target === NO_HANDLE) {
      return;
    }
    const target = this.fill(record.effects, record.count, this.times.target, nowMs, false);
    if (!sameEffects(target, this.snapshot.target)) {
      this.change({ ...this.snapshot, target });
    }
  }

  /**
   * Forgets the session: no cooldown, no refusal, no effect and no selection; every slot usable again until the next
   * `vitals`, and slots 1 to 5 the page shown where the bar pages. The item slot keeps what the bag last said it
   * holds, which the bag's own reset empties.
   */
  public reset(): void {
    this.times.cooldownEnd.fill(0);
    this.times.cooldownLength.fill(0);
    this.records.reset();
    this.times.own.count = 0;
    this.times.target.count = 0;
    this.times.frozen = false;
    this.cancelCast?.();
    this.cancelCast = null;
    this.targetHandle = NO_HANDLE;
    this.player = { dead: false, limits: 0, pools: { health: Number.POSITIVE_INFINITY, stamina: Number.POSITIVE_INFINITY, focus: Number.POSITIVE_INFINITY }, slack: NO_SLACK };
    this.snapshot = { ...this.snapshot, cast: null, own: [], target: [], frozen: false };
    this.refreshSlots({});
    this.pages.reset();
  }

  /**
   * Fills a row's times from a frame's effects and lists what it draws: the first `STATUS_MAX` effects the page's rules
   * hold, in the frame's order.
   *
   * @param entries The frame's effects.
   * @param count How many are valid.
   * @param times The row's times.
   * @param nowMs The page clock at receipt.
   * @param frozen Whether the bearer is dead, so its times stand still.
   * @returns The effects as drawn.
   */
  private fill(entries: EffectsRecord["effects"], count: number, times: RowTimes, nowMs: number, frozen: boolean): EffectView[] {
    const views: EffectView[] = [];
    const before = times.count;
    for (let at = 0; at < before; at += 1) {
      PRIOR_SPAN[at] = times.span[at] as number;
      PRIOR_EFFECT[at] = times.effect[at] as number;
    }
    for (let index = 0; index < count && views.length < STATUS_MAX; index += 1) {
      const entry = entries[index];
      const effect = entry === undefined ? undefined : effectByNumber(this.rules, entry.effect);
      if (entry === undefined || effect === undefined) {
        continue;
      }
      const at = views.length;
      const left = entry.ticksLeft * TICK_MS;
      times.left[at] = left;
      times.end[at] = frozen ? 0 : this.since(nowMs) + left;
      times.expires[at] = effect.durationTicks * TICK_MS > EXPIRING_MS ? 1 : 0;
      let span = entry.ticksLeft;
      for (let prior = 0; prior < before; prior += 1) {
        if (PRIOR_EFFECT[prior] === entry.effect) {
          span = Math.max(span, PRIOR_SPAN[prior] as number);
        }
      }
      times.effect[at] = entry.effect;
      times.span[at] = Math.min(0xffff, span);
      views.push({ effect: entry.effect, name: effect.name, glyph: EFFECT_GLYPHS[effect.id] ?? null, icon: effect.icon, tone: effectTone(effect), stacks: entry.stacks });
    }
    times.count = views.length;
    return views;
  }

  /**
   * A time on the page clock as whole ms from the origin.
   *
   * @param nowMs The page clock.
   * @returns The ms since the origin.
   */
  private since(nowMs: number): number {
    return Math.floor(nowMs) - this.times.origin;
  }

  /**
   * Recomputes every slot's state from the player's, telling the listeners when one changed or the snapshot has other
   * changes to carry.
   *
   * @param extra Other changes to the snapshot.
   */
  private refreshSlots(extra: Partial<BarSnapshot>): void {
    const slots = this.snapshot.slots;
    const changed = slots.some((slot) => slot.usable !== slotUsable(slot, this.player));
    if (!changed && Object.keys(extra).length === 0) {
      return;
    }
    this.change({ ...this.snapshot, ...extra, slots: changed ? this.specs.map((spec) => ({ ...spec, usable: slotUsable(spec, this.player) })) : slots });
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The new snapshot.
   */
  private change(next: BarSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
